import React, { useEffect, useState } from "react";
import { View, StyleSheet, ActivityIndicator, Platform } from "react-native";
import { useToast } from "../../../contexts/Toast/ToastProvider";
import { Switch } from "react-native-paper";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import CourseThumbnailUpload from "../../../components/adminDashboard/CourseThumbnailUpload/CourseThumbnailUpload";
import AddCoursePrerequisitesModal from "../../courseSettings/CourseInfoContainer/AddCoursePrerequisitesModal";
import CoursePreRequisitePill from "../../courseSettings/CourseInfoContainer/CoursePreRequisitePill";
import {
  useUpdateCourseMutation,
  useGetCourseModulesLiteQuery,
  useReGenerateCourseImageMutation,
  useGenerateModuleThumbnailsMutation,
  StoragePathType,
  Course,
} from "../../../graphql/generated/graphql";
import { PlusCircleIcon, SparkleIcon, UploadIcon } from "@phosphor-icons/react";
import { useAppConfig } from "../../../AppConfigProvider";
import useFileUpload, { generatePublicUrl, ProcessedFile } from "../../../hooks/useFileUpload";
import EnrollmentLinkManagerContainer from "./EnrollmentLinkManagerContainer";
import NewCustomAxioActionModal from "../../settings/UsersTabContainer/NewCustomAxioActionModal";

const courseInfoSchema = z.object({
  title: z.string().min(1, "Course name is required"),
  courseCode: z.string().optional(),
  courseLabel: z.string().optional(),
  isDraft: z.boolean().optional(),
  isEnrollmentAvailable: z.boolean().optional(),
  thumbnail: z.string().optional(),
});

type CourseInfoFormData = z.infer<typeof courseInfoSchema>;

enum MODAL_STATE {
  CLOSED = "CLOSED",
  PUBLISH = "PUBLISH",
  UNPUBLISH = "UNPUBLISH",
}

const modalTexts = {
  [MODAL_STATE.PUBLISH]: {
    title: "Publish Course",
    ariaLabel: "Publish Course Modal",
    warningText: "Are you sure you want to publish this course? Users will be able to view this course.",
    actionButtonText: "Save Changes",
  },
  [MODAL_STATE.UNPUBLISH]: {
    title: "Unpublish Course",
    ariaLabel: "Unpublish Course Modal",
    warningText: "Are you sure you want to unpublish this course? Users will no longer be able to view this course.",
    actionButtonText: "Save Changes",
  },
};

interface AdminCourseInformationContainerProps {
  courseId: string;
}

const AdminCourseInformationContainer: React.FC<AdminCourseInformationContainerProps> = ({ courseId }) => {
  const appConfig = useAppConfig();
  const primaryColor = appConfig.primaryColor;

  const { showToast } = useToast();

  const [isRegeneratingCourseImage, setIsRegeneratingCourseImage] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isAddingPrerequisite, setIsAddingPrerequisite] = useState(false);
  const [thumbnailToSave, setThumbnailToSave] = useState<string | null>(null);
  const [thumbnailUrlToSave, setThumbnailUrlToSave] = useState<string | null>(null);
  const [isSavingThumbnail, setIsSavingThumbnail] = useState(false);
  const [moduleThumbnailResults, setModuleThumbnailResults] = useState<{
    success: boolean;
    message: string;
    modules: Array<{ id: string; title: string; thumbnail?: string }>;
  } | null>(null);
  const [originalIsDraft, setOriginalIsDraft] = useState<boolean | null>(null);
  const [modalState, setModalState] = useState<MODAL_STATE>(MODAL_STATE.CLOSED);
  const [pendingFormData, setPendingFormData] = useState<CourseInfoFormData | null>(null);
  const {
    data: courseData,
    loading,
    error,
    refetch,
    startPolling: startCoursePolling,
    stopPolling: stopCoursePolling,
  } = useGetCourseModulesLiteQuery({
    variables: {
      id: courseId,
    },
  });

  const course = courseData?.getCourseModulesLite?.data?.[0];

  const allModulesHaveThumbnails =
    course?.modules && course.modules.length > 0
      ? course.modules.every((module) => module.thumbnail && module.thumbnail.trim() !== "")
      : false;

  const modulesGeneratingImages =
    course?.modules?.filter((module) => module.imageGenerationStatus === "generating") || [];

  const modulesWithFailedGeneration =
    course?.modules?.filter((module) => module.imageGenerationStatus === "failed") || [];

  const modulesWithoutThumbnails =
    course?.modules?.filter((module) => !module.thumbnail && module.imageGenerationStatus !== "generating") || [];

  const [updateCourse] = useUpdateCourseMutation();
  const [reGenerateCourseImage] = useReGenerateCourseImageMutation();
  const [isGeneratingModuleThumbnails, setIsGeneratingModuleThumbnails] = useState(false);
  const [generateModuleThumbnails] = useGenerateModuleThumbnailsMutation();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
  } = useForm<CourseInfoFormData>({
    resolver: zodResolver(courseInfoSchema),
    defaultValues: {
      title: "",
      courseCode: "",
      courseLabel: "",
      isDraft: false,
      isEnrollmentAvailable: false,
      thumbnail: "",
    },
  });

  const watchedValues = watch();

  useEffect(() => {
    if (course) {
      const courseIsDraft = course.isDraft || false;
      reset({
        title: course.name || "",
        courseCode: course.course_code || "",
        courseLabel: "Popular Course", // You might want to get this from course data
        isDraft: courseIsDraft,
        isEnrollmentAvailable: course.isEnrollmentAvailable || false,
        thumbnail: course.thumbnail || "",
      });
      setOriginalIsDraft(courseIsDraft);
      // Reset thumbnail to save when course data changes
      setThumbnailToSave(null);

      const modulesCurrentlyGenerating =
        course.modules?.filter((module) => module.imageGenerationStatus === "generating") || [];
      if (modulesCurrentlyGenerating.length > 0 && !isGeneratingModuleThumbnails) {
        setIsGeneratingModuleThumbnails(true);
        setModuleThumbnailResults({
          success: true,
          message: `Generating thumbnails for ${modulesCurrentlyGenerating.length} module(s)... Please wait while thumbnails are being generated`,
          modules: [],
        });
        startCoursePolling(2000);
      }
    }
  }, [course, reset, isGeneratingModuleThumbnails, startCoursePolling]);

  useEffect(() => {
    const modulesCurrentlyGenerating =
      course?.modules?.filter((module) => module.imageGenerationStatus === "generating") || [];

    if (isGeneratingModuleThumbnails || modulesCurrentlyGenerating.length > 0) {
      if (modulesCurrentlyGenerating.length > 0) {
        setModuleThumbnailResults({
          success: true,
          message: `Generating thumbnails for ${modulesCurrentlyGenerating.length} module(s)... Please wait while thumbnails are being generated`,
          modules: [],
        });
        startCoursePolling(2000);
      } else if (modulesWithFailedGeneration.length > 0) {
        stopCoursePolling();
        setIsGeneratingModuleThumbnails(false);
        setModuleThumbnailResults({
          success: false,
          message: `Failed to generate thumbnails for ${modulesWithFailedGeneration.length} module(s)`,
          modules: [],
        });
      } else if (modulesWithoutThumbnails.length === 0 && modulesCurrentlyGenerating.length === 0) {
        stopCoursePolling();
        setIsGeneratingModuleThumbnails(false);
        setModuleThumbnailResults({
          success: true,
          message: "Successfully generated thumbnails for all modules",
          modules: [],
        });
        setTimeout(() => {
          refetch();
        }, 100);
      }
    }
  }, [
    course?.modules,
    modulesWithFailedGeneration.length,
    modulesWithoutThumbnails.length,
    isGeneratingModuleThumbnails,
    startCoursePolling,
    stopCoursePolling,
    refetch,
  ]);

  const handleThumbnailChange = (newThumbnail: string, newThumbnailUrl: string) => {
    setThumbnailToSave(newThumbnail);
    setUploadError(null);
    setThumbnailUrlToSave(newThumbnailUrl);
  };

  const handleSaveThumbnail = async () => {
    if (!thumbnailToSave || !thumbnailUrlToSave) return;

    try {
      setIsSavingThumbnail(true);
      setUploadError(null);

      const response = await updateCourse({
        variables: {
          id: course.id,
          course: {
            thumbnail: thumbnailUrlToSave,
          },
        },
      });

      if (response.data?.updateCourse?.success) {
        await refetch();
        setThumbnailToSave(null);
        showToast({
          message: "Thumbnail saved successfully",
          type: "success",
        });
      } else {
        throw new Error("Failed to save thumbnail");
      }
    } catch (error) {
      console.error("Error saving thumbnail:", error);
      setUploadError("Failed to save thumbnail. Please try again.");
    } finally {
      setIsSavingThumbnail(false);
    }
  };

  const handleReGenerateCourseImage = async () => {
    try {
      setIsRegeneratingCourseImage(true);
      await reGenerateCourseImage({ variables: { courseId: course.id } });
      refetch();
      setThumbnailToSave(null);
    } catch (error) {
      console.error("Error re-generating course image:", error);
    } finally {
      setIsRegeneratingCourseImage(false);
    }
  };

  const handleGenerateModuleThumbnails = async () => {
    try {
      setIsGeneratingModuleThumbnails(true);
      setModuleThumbnailResults(null);

      const response = await generateModuleThumbnails({ variables: { courseId: course.id } });

      if (response.data?.generateModuleThumbnails) {
        const result = response.data.generateModuleThumbnails;

        if (result.success) {
          setModuleThumbnailResults({
            success: true,
            message: result.message || "Module thumbnail generation started in background",
            modules: [],
          });
          await refetch();
        } else {
          setModuleThumbnailResults({
            success: false,
            message: result.message || "Failed to start module thumbnail generation",
            modules: [],
          });
          setIsGeneratingModuleThumbnails(false);
        }
      }
    } catch (error) {
      console.error("Error generating module thumbnails:", error);
      setModuleThumbnailResults({
        success: false,
        message: "Failed to start module thumbnail generation. Please try again.",
        modules: [],
      });
      setIsGeneratingModuleThumbnails(false);
    }
  };

  const performSave = async (formData: CourseInfoFormData) => {
    try {
      const response = await updateCourse({
        variables: {
          id: course.id,
          course: {
            name: formData.title || "",
            courseCode: formData.courseCode || "",
            isDraft: formData.isDraft || false,
            isEnrollmentAvailable: formData.isEnrollmentAvailable || false,
          },
        },
      });

      if (response.data?.updateCourse?.success) {
        refetch();
        showToast({
          message: "Course updated successfully",
          type: "success",
        });
        reset(formData);
        setOriginalIsDraft(formData.isDraft || false);
      }
    } catch (error) {
      console.error("Error saving course:", error);
      showToast({
        message: "Failed to save course changes. Please try again.",
        type: "error",
      });
    }
  };

  const handleSave = handleSubmit(async (formData) => {
    const isDraftChanged = originalIsDraft !== null && formData.isDraft !== originalIsDraft;

    if (isDraftChanged) {
      setPendingFormData(formData);
      setModalState(formData.isDraft ? MODAL_STATE.UNPUBLISH : MODAL_STATE.PUBLISH);
    } else {
      await performSave(formData);
    }
  });

  const handleConfirmSave = async () => {
    if (pendingFormData) {
      await performSave(pendingFormData);
      setPendingFormData(null);
      setModalState(MODAL_STATE.CLOSED);
    }
  };

  const handleCancelSave = () => {
    setPendingFormData(null);
    setModalState(MODAL_STATE.CLOSED);
  };

  const renderBasicInformation = () => (
    <View style={styles.section}>
      <CustomText
        text="Basic Information"
        size="l"
        weight="ultraBold"
        style={styles.sectionTitle}
        useTranslationText={false}
      />

      <CustomSpacing type="vertical" size="l" />

      <View style={styles.formGroup}>
        <CustomText text="Course Name *" size="s" weight="ultraBold" style={styles.label} useTranslationText={false} />
        <CustomSpacing type="vertical" size="s" />
        <Controller
          control={control}
          name="title"
          render={({ field: { onChange, value } }) => (
            <CustomTextInput
              value={value}
              onChangeText={onChange}
              placeholder="Introduction to Biology"
              style={styles.input}
              error={errors.title?.message}
            />
          )}
        />
      </View>

      <CustomSpacing type="vertical" size="l" />

      <View style={styles.formGroup}>
        <CustomText text="Course Code" size="s" weight="ultraBold" style={styles.label} useTranslationText={false} />
        <CustomSpacing type="vertical" size="s" />
        <Controller
          control={control}
          name="courseCode"
          render={({ field: { onChange, value } }) => (
            <CustomTextInput
              value={value}
              onChangeText={onChange}
              placeholder="BIOL-101"
              style={styles.input}
              error={errors.courseCode?.message}
            />
          )}
        />
      </View>

      <CustomSpacing type="vertical" size="l" />

      <View style={styles.statusContainer}>
        <View style={styles.statusGroup}>
          <CustomText
            text="Course Status"
            size="s"
            weight="ultraBold"
            style={styles.label}
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="s" />
          <Controller
            control={control}
            name="isDraft"
            render={({ field: { onChange, value } }) => (
              <View style={styles.switchContainer}>
                <CustomText text="Published" size="s" weight="medium" useTranslationText={false} />
                <Switch value={!value} onValueChange={(newValue) => onChange(!newValue)} color={primaryColor} />
              </View>
            )}
          />
        </View>

        <View style={styles.statusGroup}>
          <CustomText
            text="Enrollment Status"
            size="s"
            weight="ultraBold"
            style={styles.label}
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="s" />
          <Controller
            control={control}
            name="isEnrollmentAvailable"
            render={({ field: { onChange, value } }) => (
              <View style={styles.switchContainer}>
                <CustomText text="Enabled" size="s" weight="medium" useTranslationText={false} />
                <Switch value={value} onValueChange={onChange} color={primaryColor} />
              </View>
            )}
          />
          <CustomSpacing type="vertical" size="xs" />
          <CustomText
            text="Note: Even if enrollment is disabled, users can still enroll using a valid enrollment link."
            size="xs"
            style={styles.sectionDescription}
            useTranslationText={false}
          />
        </View>
      </View>

      <CustomSpacing type="vertical" size="xl" />

      <CustomButton
        text="Save Changes"
        styleType="light"
        size="m"
        onPress={handleSave}
        useTranslationText={false}
        style={[styles.saveButton, !isDirty && styles.saveButtonDisabled]}
        textStyle={!isDirty ? styles.saveButtonTextDisabled : undefined}
        disabled={!isDirty}
      />
    </View>
  );

  const renderCoursePrerequisites = () => (
    <View style={styles.section}>
      <CustomText
        text="Course Prerequisites"
        size="l"
        weight="ultraBold"
        style={styles.sectionTitle}
        useTranslationText={false}
      />

      <CustomSpacing type="vertical" size="s" />

      <CustomText
        text="Add courses that students must complete before enrolling in this course."
        size="s"
        style={styles.sectionDescription}
        useTranslationText={false}
      />

      <CustomSpacing type="vertical" size="l" />

      {course.preRequisites && course.preRequisites.length > 0 && (
        <View style={styles.prerequisitesList}>
          {course.preRequisites
            ?.filter((prerequisite) => prerequisite.id !== course.id)
            .map((prerequisite) => (
              <CoursePreRequisitePill
                key={prerequisite.id}
                courseId={course.id}
                prerequisite={prerequisite as Course}
                refetchCourse={refetch}
              />
            ))}
        </View>
      )}

      {(!course.preRequisites || course.preRequisites.length === 0) && (
        <CustomText text="No prerequisites assigned" size="s" style={styles.noDataText} useTranslationText={false} />
      )}

      <CustomSpacing type="vertical" size="l" />

      <CustomButton
        text="Add Prerequisites"
        styleType="primaryLight"
        leftIcon={<PlusCircleIcon size={16} color={primaryColor} />}
        onPress={() => setIsAddingPrerequisite(true)}
        useTranslationText={false}
        style={styles.addButton}
      />
    </View>
  );

  const { currentDomain } = useAppConfig();

  const { handleFileUpload } = useFileUpload({
    storagePath: StoragePathType.Thumbnails,
    onError: (error) => setUploadError(error),
    afterUpload: (processedFilesArray: ProcessedFile[]) => {
      handleThumbnailChange(
        processedFilesArray[0].localUploadedFile.uri,
        generatePublicUrl(currentDomain, processedFilesArray[0].file.filePath)
      );
    },
    isPublic: true,
  });

  const renderCourseThumbnail = () => (
    <View style={styles.section}>
      <CustomText
        text="Course Thumbnail"
        size="l"
        weight="ultraBold"
        style={styles.sectionTitle}
        useTranslationText={false}
      />

      <CustomSpacing type="vertical" size="s" />

      <CustomText
        text="Upload a thumbnail image for the course. Size: 896x504 pixels (16:9 ratio), JPG or PNG, max 2MB."
        size="s"
        style={styles.sectionDescription}
        useTranslationText={false}
      />

      <CustomSpacing type="vertical" size="l" />

      <View style={styles.thumbnailContainer}>
        <CourseThumbnailUpload
          value={thumbnailToSave || course.thumbnail}
          onChange={handleThumbnailChange}
          onError={(error) => setUploadError(error)}
        />
      </View>

      <CustomSpacing type="vertical" size="m" />

      <View style={styles.thumbnailActions}>
        {!thumbnailToSave ? (
          <CustomButton
            text="Upload Image"
            styleType="primaryLight"
            size="s"
            leftIcon={<UploadIcon size={16} color={primaryColor} />}
            onPress={handleFileUpload}
            useTranslationText={false}
            style={styles.thumbnailActionButton}
          />
        ) : (
          <CustomButton
            text={isSavingThumbnail ? "Saving..." : "Save Thumbnail"}
            styleType="primary"
            size="s"
            leftIcon={
              isSavingThumbnail ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <UploadIcon size={16} color="#FFFFFF" />
              )
            }
            onPress={handleSaveThumbnail}
            disabled={isSavingThumbnail}
            useTranslationText={false}
            style={styles.saveThumbnailButton}
          />
        )}

        {isRegeneratingCourseImage ? (
          <CustomButton
            text="Generating..."
            styleType="primaryLight"
            size="s"
            leftIcon={<ActivityIndicator size="small" color={primaryColor} />}
            onPress={() => {}}
            disabled={true}
            useTranslationText={false}
            style={styles.thumbnailActionButton}
          />
        ) : (
          <CustomButton
            text="Regenerate"
            styleType="primaryLight"
            size="s"
            leftIcon={<SparkleIcon size={16} color={primaryColor} />}
            onPress={handleReGenerateCourseImage}
            useTranslationText={false}
            style={styles.thumbnailActionButton}
          />
        )}
      </View>

      {uploadError && (
        <>
          <CustomSpacing type="vertical" size="s" />
          <CustomText text={uploadError} size="xs" style={styles.errorText} useTranslationText={false} />
        </>
      )}
    </View>
  );

  const renderModuleThumbnails = () => (
    <View style={styles.section}>
      <CustomText text="Module Thumbnails" size="l" weight="ultraBold" style={styles.sectionTitle} />
      <CustomSpacing type="vertical" size="s" />
      <CustomText text="Generate images for all modules in the course." size="s" style={styles.sectionDescription} />
      <CustomSpacing type="vertical" size="l" />

      {isGeneratingModuleThumbnails ? (
        <CustomButton
          text="Generating..."
          styleType="primaryLight"
          size="s"
          leftIcon={<ActivityIndicator size="small" color={primaryColor} />}
          onPress={() => {}}
          disabled={true}
          useTranslationText={false}
          style={styles.thumbnailActionButton}
        />
      ) : (
        <CustomButton
          text="Generate module thumbnails"
          styleType="primaryLight"
          size="s"
          leftIcon={<SparkleIcon size={16} color={primaryColor} />}
          onPress={handleGenerateModuleThumbnails}
          disabled={allModulesHaveThumbnails || isGeneratingModuleThumbnails}
          style={
            allModulesHaveThumbnails
              ? [styles.thumbnailActionButton, styles.disabledButton]
              : styles.thumbnailActionButton
          }
        />
      )}

      {moduleThumbnailResults && (
        <>
          <CustomSpacing type="vertical" size="m" />
          {!moduleThumbnailResults.success && (
            <CustomText
              text={moduleThumbnailResults.message || "Failed to generate module thumbnails"}
              size="xs"
              style={styles.errorText}
              useTranslationText={false}
            />
          )}
          {moduleThumbnailResults.success && (
            <View>
              <CustomText
                text={moduleThumbnailResults.message || "Thumbnail generation started successfully"}
                size="xs"
                style={styles.successText}
                useTranslationText={false}
              />
              {isGeneratingModuleThumbnails && (
                <>
                  <CustomSpacing type="vertical" size="xs" />
                  <CustomText
                    text="⏳ This process may take several minutes. You can continue using the app while thumbnails are being generated."
                    size="xs"
                    style={styles.infoText}
                    useTranslationText={false}
                  />
                </>
              )}
            </View>
          )}
        </>
      )}
    </View>
  );

  if (loading || !course) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0000ff" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <CustomText text="Error loading course" size="l" weight="ultraBold" useTranslationText={false} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.responsiveLayout}>
        {renderBasicInformation()}
        {renderCoursePrerequisites()}
        {renderCourseThumbnail()}
        {renderModuleThumbnails()}
      </View>

      <CustomSpacing type="vertical" size="xl" />

      <EnrollmentLinkManagerContainer courseId={courseId} />

      <AddCoursePrerequisitesModal
        visible={isAddingPrerequisite}
        onClose={() => setIsAddingPrerequisite(false)}
        course={course as Course}
        refetchCourse={refetch}
      />

      {modalState !== MODAL_STATE.CLOSED && (
        <NewCustomAxioActionModal
          visible={true}
          onClose={handleCancelSave}
          modalTexts={modalTexts[modalState]}
          onAction={handleConfirmSave}
          contextualText={course?.name}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 32,
    backgroundColor: "#FFFFFF",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  responsiveLayout: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 32,
  },
  section: {
    backgroundColor: "#FFFFFF",
    minWidth: "47%",
    flex: 1,
    maxWidth: "47%",
  },
  sectionTitle: {
    color: "#111827",
    fontSize: 18,
  },
  sectionDescription: {
    color: "#6B7280",
    lineHeight: 20,
  },
  formGroup: {
    width: "100%",
  },
  label: {
    color: "#111827",
    fontSize: 14,
  },
  input: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 14,
    backgroundColor: "#FFFFFF",
  },
  statusContainer: {
    flexDirection: "row",
    gap: 24,
  },
  statusGroup: {
    flex: 1,
  },
  switchContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  prerequisitesList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  saveButton: {
    backgroundColor: "#F3F4F6",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    alignSelf: "flex-start",
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonTextDisabled: {
    color: "#9CA3AF",
  },
  noDataText: {
    color: "#9CA3AF",
    fontStyle: "italic",
  },
  addButton: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#3B82F6",
    alignSelf: "stretch",
    alignItems: "center",
    justifyContent: "center",
  },
  thumbnailContainer: {
    alignItems: "flex-start",
  },
  thumbnailActions: {
    flexDirection: "row",
    gap: 12,
    flexWrap: "wrap",
  },
  thumbnailActionButton: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#D1D5DB",
  },
  saveThumbnailButton: {
    backgroundColor: "#3B82F6",
  },
  errorText: {
    color: "#EF4444",
  },
  successText: {
    color: "#10B981",
  },
  infoText: {
    color: "#6B7280",
  },
  disabledButton: {
    opacity: 0.6,
  },
});

export default AdminCourseInformationContainer;
